import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { OrganizationEvents } from "@app/shared/src/api/api.gen";
import { FacilityEvent, FacilityEventType } from "@app/shared/src/models";
import {
    Btn,
    Card,
    Combo,
    DownloadIcon,
    Note,
    Select,
    Spinner,
    Table,
    TableBar,
    Td,
    Th,
    formatDateTime,
    timeZoneLabel,
    useInfiniteScroll,
} from "@app/shared/src/components_v2";
import { siteLabel } from "../../helpers/deviceLabels";
import { EventTag, ReviewMark, eventLabel, eventMeasure, reviewStatusText } from "../../helpers/eventDisplay";
import { usePoll } from "../../hooks/usePoll";
import { useZoneIndex } from "../../hooks/useZoneNames";
import { urlFlag, urlNumberOrAll, urlOptionalId, urlOptionalNumber, useUrlState } from "../../hooks/useUrlState";
import { useFleet } from "../providers/FleetProvider";
import { useOrg } from "../providers/OrgProvider";
import { PageTitle } from "../shared/PageTitle";

/**
 * Fleet-wide event browser: what fired, on which truck, at what speed, and which relays it drove —
 * with the full incident report one click away.
 *
 * Pages in as the reader scrolls rather than behind a button, because working down an event log is
 * a continuous motion. The CSV exports exactly the rows on screen, since "the filtered view, in a
 * spreadsheet" is what gets attached to incident emails.
 */

const PAGE = 50;
const REFRESH_MS = 60_000;

const TYPE_FILTERS: { label: string; value?: FacilityEventType }[] = [
    { label: "All event types" },
    { label: "Impending collision / pass", value: FacilityEventType.ImpendingZoneEntry },
    { label: "Speeding", value: FacilityEventType.Speeding },
];

const RANGE_FILTERS = [
    { label: "Last 24 hours", hours: 24 },
    { label: "Last 7 days", hours: 24 * 7 },
    { label: "Last 14 days", hours: 24 * 14 },
    { label: "All time", hours: undefined as number | undefined },
];

export const EventsPage: React.FC = () => {
    const { org } = useOrg();
    // Trucks only, so the unit picker cannot offer a traffic counter this fleet never deployed.
    const { statsDevices: devices, deviceSite, deviceKey } = useFleet();
    const zones = useZoneIndex();
    const navigate = useNavigate();

    // Every filter lives in the URL: this is the view people paste into incident emails, and Back
    // has to walk out of a narrowing one step at a time rather than leaving the page.
    const [type, setType] = useUrlState<FacilityEventType | undefined>("type", undefined, urlOptionalNumber);
    // urlNumberOrAll, not urlOptionalNumber: "All time" is undefined here but 24 is the default, so
    // the two have to stay tellable apart in the URL.
    const [rangeHours, setRangeHours] = useUrlState<number | undefined>("hours", 24, urlNumberOrAll);
    // Events a reviewer judged false positives are out of the way by default — that is the point
    // of marking them. The toggle is how you go back and audit those calls.
    const [showInvalid, setShowInvalid] = useUrlState<boolean>("invalid", false, urlFlag);
    const [deviceId, setDeviceId] = useUrlState<string | undefined>("device", undefined, urlOptionalId);

    // A unit picked before the group changed has dropped out of scope.
    useEffect(() => {
        if (deviceId && devices.length > 0 && !devices.some((d) => d.id === deviceId)) {
            setDeviceId(undefined);
        }
    }, [deviceId, devices, setDeviceId]);

    const [events, setEvents] = useState<FacilityEvent[]>([]);
    const [cursor, setCursor] = useState<string>();
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isPaging, setIsPaging] = useState(false);
    const [error, setError] = useState<string>();
    const seq = useRef(0);

    const dateFrom = useMemo(() => (rangeHours ? new Date(Date.now() - rangeHours * 3_600_000) : undefined), [rangeHours]);

    // Filters reset the list; scrolling appends to it.
    const loadFirst = useCallback(async () => {
        if (!org?.id) return;
        const mySeq = ++seq.current;
        setIsLoading(true);
        setError(undefined);
        try {
            const page = await OrganizationEvents.getByOrganization(
                org.id, PAGE, "dateCreated", "desc", type, deviceId, undefined, undefined, undefined, dateFrom,
                undefined, undefined, undefined, showInvalid,
            );
            if (seq.current !== mySeq) return;
            setEvents(page?.data ?? []);
            setCursor(page?.nextCursor);
            setHasMore(Boolean(page?.hasMore));
        } catch (err) {
            if (seq.current === mySeq) setError(err instanceof Error ? err.message : "Could not load events");
        } finally {
            if (seq.current === mySeq) setIsLoading(false);
        }
    }, [org?.id, type, deviceId, dateFrom, showInvalid]);

    useEffect(() => {
        loadFirst();
    }, [loadFirst]);

    const loadMore = useCallback(async () => {
        if (!org?.id || !cursor || isPaging) return;
        const mySeq = seq.current;
        setIsPaging(true);
        try {
            const page = await OrganizationEvents.getByOrganization(
                org.id, PAGE, "dateCreated", "desc", type, deviceId, undefined, undefined, undefined, dateFrom, undefined, cursor,
                undefined, showInvalid,
            );
            // A filter change mid-flight invalidates this page.
            if (seq.current !== mySeq) return;
            setEvents((prev) => [...prev, ...(page?.data ?? [])]);
            setCursor(page?.hasMore ? page?.nextCursor : undefined);
            setHasMore(Boolean(page?.hasMore));
        } finally {
            setIsPaging(false);
        }
    }, [org?.id, cursor, isPaging, type, deviceId, dateFrom, showInvalid]);

    const sentinel = useInfiniteScroll(loadMore, { enabled: hasMore && !isLoading, isLoading: isPaging });

    // New events land while the log is open. Refresh the top of the list, but leave a reader who
    // has paged in alone — yanking them back to page one mid-scroll is worse than a minute of lag.
    usePoll(() => {
        if (!isPaging && events.length <= PAGE) loadFirst();
    }, REFRESH_MS);

    /**
     * The fleet scope, applied after the rows land: the event query takes one unit or one site,
     * never a set. Paging keeps pulling while the sentinel is on screen, so a page that turns out
     * to be mostly other units' events fills itself in rather than reading as the end of the log.
     *
     * Applied whether or not a group is chosen: "All groups" means every crew, not every sensor in
     * the organization.
     */
    const fleetIds = useMemo(() => new Set(devices.map((d) => d.id)), [devices]);
    const rows = useMemo(
        () => events.filter((e) => e.deviceId && fleetIds.has(e.deviceId)),
        [events, fleetIds],
    );

    const zoneOf = (e: FacilityEvent) => (e.detectionZoneId ? zones.get(e.detectionZoneId) : undefined);

    /**
     * Which relays this event drove. The server records the event, not the trigger configuration
     * that fired it, so this is the trigger's wiring as it stands — flagged when the trigger has
     * been edited since, because then it may not be what actually happened.
     */
    const relaysFor = (e: FacilityEvent) => {
        const zone = zoneOf(e);
        if (!zone) return { text: "—", stale: false };
        const firedAt = new Date(e.dateCreated ?? 0).getTime();
        const stale = zone.lastModified !== undefined && zone.lastModified.getTime() > firedAt;
        return { text: zone.relays.length > 0 ? zone.relays.join(" + ") : "—", stale };
    };

    const exportCsv = () => {
        const zone = "Time";
        const csvRows = [
            [`${zone} (${timeZoneLabel()})`, "TMA", "Device key", "Event", "Closest / speed", "Relays", "Clip", "Review", "Review notes"],
            ...rows.map((e) => [
                formatDateTime(e.dateCreated),
                deviceSite(e.deviceId),
                deviceKey(e.deviceId) ?? "",
                eventLabel(e, zoneOf(e)?.name),
                eventMeasure(e)?.text ?? "",
                relaysFor(e).text,
                // Either route counts as having footage — the token the trigger stamped, or a media
                // request the server matched to this event's target. Reading only the token reported
                // "no" for clips that exist and play.
                //
                // The second half is hasClipVideo, not clipRequestId: a request is matched by window
                // overlap whatever became of it, so an expired or cancelled one still sets the id.
                // Answering the id would trade the original false "no" for a false "yes", which is
                // worse in a column someone exports to decide what footage to go and review.
                e.clipAccessToken || e.hasClipVideo ? "yes" : "no",
                reviewStatusText(e),
                e.reviewNotes ?? "",
            ]),
        ];
        const csv = csvRows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        a.download = `fleet-commander-events-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    if (error) return <Note tone="crit">{error}</Note>;

    return (
        <div className="flex flex-col">
            <TableBar title={<PageTitle>Events</PageTitle>}>
                <Select
                    value={type === undefined ? "" : String(type)}
                    onChange={(e) => setType(e.target.value === "" ? undefined : (Number(e.target.value) as FacilityEventType))}
                >
                    {TYPE_FILTERS.map((f) => (
                        <option key={f.label} value={f.value === undefined ? "" : String(f.value)}>
                            {f.label}
                        </option>
                    ))}
                </Select>

                <Combo
                    value={deviceId ?? "all"}
                    // The picker's "all" is the absence of a filter, not a device id to store.
                    onChange={(v) => setDeviceId(v === "all" ? undefined : v)}
                    options={[
                        { value: "all", label: "All units" },
                        ...devices.map((d) => ({ value: d.id!, label: siteLabel(d), sub: d.deviceKey })),
                    ]}
                    placeholder="Search TMA or device key…"
                />

                <Select value={String(rangeHours ?? "")} onChange={(e) => setRangeHours(e.target.value === "" ? undefined : Number(e.target.value))}>
                    {RANGE_FILTERS.map((f) => (
                        <option key={f.label} value={f.hours === undefined ? "" : String(f.hours)}>
                            {f.label}
                        </option>
                    ))}
                </Select>

                <label className="inline-flex min-h-9 cursor-pointer select-none items-center gap-2 rounded-[10px] border border-[var(--line-2)] bg-[var(--surface)] px-3 text-[12.5px] font-medium">
                    <input
                        type="checkbox"
                        checked={showInvalid}
                        onChange={(e) => setShowInvalid(e.target.checked)}
                        className="h-3.5 w-3.5 accent-[var(--accent)]"
                    />
                    Show invalid
                </label>

                <Btn tone="quiet" onClick={exportCsv} disabled={rows.length === 0}>
                    <DownloadIcon size={15} />
                    CSV
                </Btn>
            </TableBar>

            <Card>
                {isLoading ? (
                    <div className="grid place-items-center p-10 text-[var(--muted)]">
                        <Spinner />
                    </div>
                ) : rows.length === 0 ? (
                    <p className="px-5 py-6 text-[13px] text-[var(--muted)]">No events match those filters.</p>
                ) : (
                    <Table>
                            <thead>
                                <tr>
                                    <Th>Time ({timeZoneLabel()})</Th>
                                    <Th>TMA</Th>
                                    <Th>Event</Th>
                                    {/* One column, two meanings — near-miss margin for a collision,
                                        speed for everything else. See eventMeasure. */}
                                    <Th>Closest / speed</Th>
                                    <Th className="text-right">Reviewed</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((e) => {
                                    const measure = eventMeasure(e);
                                    return (
                                        <tr
                                            key={e.id}
                                            className={`cursor-pointer hover:bg-[var(--surface-2)] ${
                                                e.isValidAlert === false ? "opacity-55" : ""
                                            }`}
                                            onClick={() => navigate(`/events/${e.id}`, { state: { event: e, from: "/events" } })}
                                        >
                                            <Td label="Time" className="tabular-nums">{formatDateTime(e.dateCreated)}</Td>
                                            {/* Site over device name: one location is one TMA is one
                                                unit, and the TMA number is what an incident is filed
                                                under. The key underneath names the box itself. */}
                                            <Td label="TMA">
                                                <span className="block font-semibold">{deviceSite(e.deviceId)}</span>
                                                <span className="block font-mono text-[12px] text-[var(--muted)]">
                                                    {deviceKey(e.deviceId) ?? "—"}
                                                </span>
                                            </Td>
                                            <Td label="Event">
                                                <EventTag event={e} zoneName={zoneOf(e)?.name} />
                                            </Td>
                                            <Td label="Closest / speed" className={`tabular-nums ${measure?.severe ? "text-[var(--crit)]" : ""}`} title={measure?.title}>
                                                {measure?.text ?? "—"}
                                            </Td>
                                            <Td label="Reviewed" className="text-right">
                                                <ReviewMark event={e} />
                                            </Td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                    </Table>
                )}

                {/* Scrolling here pulls the next page. */}
                <div ref={sentinel} />
                {isPaging && (
                    <div className="grid place-items-center py-5 text-[var(--muted)]">
                        <Spinner />
                    </div>
                )}
            </Card>

            {/* The relay column is gone, so the asterisk it explained goes with it. Relay wiring
                still rides along in the CSV, and the incident report carries the full picture. */}
        </div>
    );
};
